import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, ArrowLeft, Share2, Clock } from "lucide-react";
import axios from "axios";
import { Header, Footer, CTASection } from "../components/Orchestrio";
import { API } from "../App";
import SEO, { BlogPostSchema } from "../components/SEO";

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/public/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error("Error fetching post:", error);
        setError("Post not found");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', { 
      day: 'numeric', month: 'long', year: 'numeric' 
    });
  };

  const estimateReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content?.split(/\s+/).length || 0;
    return `${Math.ceil(words / wordsPerMinute)} min read`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0E0E0E]">
        <Header />
        <div className="pt-32 pb-16 px-6">
          <div className="max-w-3xl mx-auto animate-pulse">
            <div className="h-8 bg-[#2E2E2E] rounded w-3/4 mb-4" />
            <div className="h-4 bg-[#2E2E2E] rounded w-1/4 mb-8" />
            <div className="aspect-video bg-[#2E2E2E] mb-8" />
            <div className="space-y-4">
              <div className="h-4 bg-[#2E2E2E] rounded w-full" />
              <div className="h-4 bg-[#2E2E2E] rounded w-full" />
              <div className="h-4 bg-[#2E2E2E] rounded w-2/3" />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#0E0E0E]">
        <Header />
        <div className="pt-32 pb-16 px-6 text-center">
          <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
          <p className="text-[#8A8A8A] mb-8">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0E0E0E]" data-testid="blog-post-page">
      <SEO 
        title={post.title}
        description={post.excerpt}
        type="article"
        image={post.featured_image}
        article={{
          publishedAt: post.published_at,
          author: post.author,
          tags: post.tags
        }}
      />
      <BlogPostSchema post={post} />
      <Header />

      {/* Article */}
      <article className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-[#8A8A8A] hover:text-[#5F8FB8] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>

          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-4 text-[#8A8A8A] text-sm mb-4">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.published_at)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {estimateReadTime(post.content)}
              </span>
            </div>

            <h1 className="text-headline font-bold mb-6">{post.title}</h1>

            {post.excerpt && (
              <p className="text-body-lg text-[#8A8A8A]">{post.excerpt}</p>
            )}

            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6">
                {post.tags.map((tag, index) => (
                  <span key={index} className="badge badge-neutral">{tag}</span>
                ))}
              </div>
            )}
          </header>

          {post.featured_image && (
            <div className="mb-12">
              <img 
                src={post.featured_image} 
                alt={post.title}
                className="w-full aspect-video object-cover border border-[#2E2E2E]"
              />
            </div>
          )}

          {/* Article Content */}
          <div 
            className="prose prose-invert prose-lg max-w-none
              prose-headings:font-bold prose-headings:tracking-tight
              prose-headings:font-[Space_Grotesk,sans-serif]
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-[#F5F4EF] prose-p:leading-relaxed prose-p:mb-6
              prose-a:text-[#5F8FB8] prose-a:no-underline hover:prose-a:underline
              prose-strong:text-[#F5F4EF]
              prose-ul:my-6 prose-li:text-[#F5F4EF]
              prose-blockquote:border-l-[#5F8FB8] prose-blockquote:text-[#8A8A8A]
              prose-code:text-[#5F8FB8] prose-code:bg-[#1C1C1C] prose-code:px-1.5 prose-code:py-0.5
              prose-pre:bg-[#1C1C1C] prose-pre:border prose-pre:border-[#2E2E2E]
            "
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-[#2E2E2E]">
            <div className="flex items-center justify-between">
              <span className="text-[#8A8A8A] text-sm">Share this article</span>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copied to clipboard!');
                }}
                className="flex items-center gap-2 text-[#8A8A8A] hover:text-[#5F8FB8] transition-colors"
              >
                <Share2 className="w-4 h-4" /> Copy Link
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <CTASection
        title="Need Help With Your Project"
        description="Let's discuss how we can help you achieve your goals."
        primaryCta="Get in touch"
        primaryCtaLink="/contact"
      />

      <Footer />
    </div>
  );
};

export default BlogPostPage;
